import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import {
  BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, LineChart, Line, Legend,
} from "recharts";

/* ============================================================================
   PRO FORMA CORE  (ported from the pure modules — identical math, plain JS)
   ============================================================================ */
function annualDebtService(principal, rate, amortYears) {
  if (principal <= 0) return 0;
  if (rate === 0) return principal / amortYears;
  return (principal * rate) / (1 - Math.pow(1 + rate, -amortYears));
}
function irr(cfs) {
  const npv = (r) => cfs.reduce((a, cf, t) => a + cf / Math.pow(1 + r, t), 0);
  let lo = -0.9, hi = 1.0, fLo = npv(lo), fHi = npv(hi);
  if (fLo * fHi > 0) return null;
  for (let i = 0; i < 200; i++) {
    const mid = (lo + hi) / 2, fMid = npv(mid);
    if (Math.abs(fMid) < 1e-7) return mid;
    if (fLo * fMid < 0) { hi = mid; } else { lo = mid; fLo = fMid; }
  }
  return (lo + hi) / 2;
}
function computeProForma(input) {
  const {
    perpInterestRate, amortYears, loanTermYears, taxCreditPrice, annualCredits,
    softSourcesAvailable, totalDevelopmentCost, grossPotentialRent, otherIncome,
    vacancyRate, badDebtRate, operatingExpenses, replacementReservePerUnit, unitCount,
    rentGrowth, opexGrowth, exitCapRate, leaseUpDelayMonths, horizonYears, sizingDscr,
  } = input;

  const reserves = replacementReservePerUnit * unitCount;
  const egi1 = (grossPotentialRent + otherIncome) * (1 - vacancyRate - badDebtRate);
  const noi1 = egi1 - (operatingExpenses + reserves);

  const maxDS = noi1 / sizingDscr;
  const r = perpInterestRate, n = amortYears;
  const loanProceeds = r === 0 ? maxDS * n : (maxDS * (1 - Math.pow(1 + r, -n))) / r;
  const debtService = annualDebtService(loanProceeds, perpInterestRate, amortYears);

  const equityRaised = annualCredits * taxCreditPrice * 10;
  const fundingGap = totalDevelopmentCost - (loanProceeds + equityRaised + softSourcesAvailable);

  const leaseUp = Math.max(0, 1 - leaseUpDelayMonths / 12);
  const schedule = [];
  for (let y = 1; y <= horizonYears; y++) {
    const grow = (b, g) => b * Math.pow(1 + g, y - 1);
    const egi = (grow(grossPotentialRent, rentGrowth) + grow(otherIncome, rentGrowth)) * (1 - vacancyRate - badDebtRate);
    const opex = grow(operatingExpenses, opexGrowth) + reserves;
    let noi = egi - opex;
    if (y === 1) noi *= leaseUp;
    const cashFlow = noi - debtService;
    const dscr = debtService > 0 ? noi / debtService : Infinity;
    schedule.push({ year: y, noi, opex, debtService, cashFlow, dscr });
  }
  const exitYear = Math.min(loanTermYears, horizonYears);
  const exitNoi = schedule[exitYear - 1]?.noi ?? 0;
  const residualValue = exitCapRate > 0 ? exitNoi / exitCapRate : 0;

  const eq = [-equityRaised];
  for (let y = 1; y <= horizonYears; y++) {
    let cf = Math.max(0, schedule[y - 1].cashFlow) * 0.5;
    if (y === horizonYears) cf += residualValue * 0.5;
    eq.push(cf);
  }
  const investorIrr = irr(eq);
  const dscrs = schedule.map((s) => s.dscr).filter(isFinite);
  return {
    schedule,
    minDscr: dscrs.length ? Math.min(...dscrs) : Infinity,
    fundingGap, loanProceeds, equityRaised, residualValue,
    year15CashFlow: schedule[horizonYears - 1]?.cashFlow ?? 0,
    investorIrr,
  };
}

/* ---- constraints ---- */
function evaluateConstraints(out, cfg) {
  return [
    { id: "min_dscr", label: `Min DSCR ≥ ${cfg.minDscr.toFixed(2)}`, actual: out.minDscr, threshold: cfg.minDscr, pass: out.minDscr >= cfg.minDscr },
    { id: "investor_irr", label: `Investor IRR ≥ ${(cfg.minIrr * 100).toFixed(1)}%`, actual: out.investorIrr ?? -Infinity, threshold: cfg.minIrr, pass: (out.investorIrr ?? -Infinity) >= cfg.minIrr },
    { id: "funding_gap", label: `Funding gap ≤ 0`, actual: out.fundingGap, threshold: 0, pass: out.fundingGap <= 0 },
  ];
}
const allPass = (rs) => rs.every((r) => r.pass);

/* ---- distributions ---- */
function makeRng(seed) {
  let a = seed >>> 0;
  return function () {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function sampleTri(rng, min, mode, max) {
  const u = rng(), c = (mode - min) / (max - min);
  if (u < c) return min + Math.sqrt(u * (max - min) * (mode - min));
  return max - Math.sqrt((1 - u) * (max - min) * (max - mode));
}

/* ---- pressure test ---- */
function metricOf(out, metric) {
  if (metric === "fundingGap") return out.fundingGap;
  if (metric === "minDscr") return out.minDscr;
  if (metric === "investorIrr") return out.investorIrr ?? 0;
  return 0;
}
function runTornado(base, spec, metric) {
  const baseM = metricOf(computeProForma(base), metric);
  return Object.keys(spec).map((k) => {
    const pct = spec[k];
    const lo = computeProForma({ ...base, [k]: base[k] * (1 - pct) });
    const hi = computeProForma({ ...base, [k]: base[k] * (1 + pct) });
    const a = metricOf(lo, metric), b = metricOf(hi, metric);
    const low = Math.min(a, b), high = Math.max(a, b);
    return { key: k, label: LABELS[k] || k, low, high, baseM, swing: high - low };
  }).sort((x, y) => y.swing - x.swing);
}
function runScenarios(base, scenarios, cfg) {
  return scenarios.map(({ name, overrides }) => {
    const outputs = computeProForma({ ...base, ...overrides });
    const constraints = evaluateConstraints(outputs, cfg);
    return { name, outputs, constraints, pass: allPass(constraints) };
  });
}

/* ============================================================================
   PRESENTATION
   ============================================================================ */
const INK = "#F1F6FC", PANEL = "#FFFFFF", LINE = "#D9E3F2";
const TEXT = "#0B1B4A", MUTE = "#5A6A8C", FAINT = "#94A1BC";
const ACCENT = "#0044EB", SECONDARY = "#F44380";            // interactive / controllable
const PASS = "#1F9D57", MARGIN = "#E08A2C", BREACH = "#E11D48";

const LABELS = {
  perpInterestRate: "Perm interest rate",
  opexGrowth: "OpEx growth",
  rentGrowth: "Rent growth",
  vacancyRate: "Vacancy + bad debt",
  totalDevelopmentCost: "Total dev cost",
  taxCreditPrice: "Tax-credit price",
  exitCapRate: "Exit cap rate",
  leaseUpDelayMonths: "Lease-up delay",
};

const BASE_INPUTS = {
  perpInterestRate: 0.0625, amortYears: 35, loanTermYears: 17,
  taxCreditPrice: 0.92, annualCredits: 1_200_000, softSourcesAvailable: 6_000_000,
  totalDevelopmentCost: 28_000_000, grossPotentialRent: 2_400_000, otherIncome: 120_000,
  vacancyRate: 0.06, badDebtRate: 0.0, operatingExpenses: 1_300_000,
  replacementReservePerUnit: 350, unitCount: 80, rentGrowth: 0.02, opexGrowth: 0.03,
  exitCapRate: 0.055, leaseUpDelayMonths: 3, horizonYears: 15, sizingDscr: 1.15,
};

const fmtM = (v) => (v == null ? "—" : (v < 0 ? "−" : "") + "$" + (Math.abs(v) / 1e6).toFixed(2) + "M");
const fmtPct = (v, d = 1) => (v == null ? "—" : (v * 100).toFixed(d) + "%");
const fmtX = (v) => (v == null || !isFinite(v) ? "—" : v.toFixed(2) + "×");

/* dscr -> semantic color against floor */
function dscrColor(v, floor) {
  if (v < floor) return BREACH;
  if (v < floor + 0.05) return MARGIN;
  return PASS;
}
function gapColor(v) {
  if (v > 250_000) return BREACH;
  if (v > 0) return MARGIN;
  return PASS;
}
function irrColor(v, hurdle) {
  if (v == null || v < hurdle) return BREACH;
  if (v < hurdle + 0.015) return MARGIN;
  return PASS;
}

function Kpi({ label, value, color, sub }) {
  return (
    <div style={{ background: PANEL, border: `1px solid ${LINE}`, borderRadius: 10, padding: "14px 16px", flex: 1, minWidth: 132, boxShadow: "0 1px 4px rgba(11,27,74,.05)" }}>
      <div style={{ fontSize: 10.5, letterSpacing: ".08em", textTransform: "uppercase", color: FAINT, marginBottom: 8 }}>{label}</div>
      <div style={{ fontFamily: "var(--mono)", fontSize: 26, fontWeight: 600, color, lineHeight: 1, letterSpacing: "-.01em" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: MUTE, marginTop: 7, fontFamily: "var(--mono)" }}>{sub}</div>}
    </div>
  );
}

function Panel({ title, note, children, right }) {
  return (
    <div style={{ background: PANEL, border: `1px solid ${LINE}`, borderRadius: 12, padding: 18, boxShadow: "0 1px 4px rgba(11,27,74,.05)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4, flexWrap: "wrap", gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: 13, fontWeight: 600, letterSpacing: ".02em", color: TEXT }}>{title}</h3>
        {right}
      </div>
      {note && <div style={{ fontSize: 11.5, color: FAINT, marginBottom: 14 }}>{note}</div>}
      {children}
    </div>
  );
}

/* ---- vector chart drawers for the PDF (crisp, brand-coloured, fully axis-labelled) ---- */
const PDF_BLUE = [0, 68, 235], PDF_PINK = [214, 51, 108], PDF_RED = [225, 29, 72], PDF_GREEN = [31, 157, 87], PDF_GRID = [225, 231, 242], PDF_AXIS = [120, 134, 160], PDF_NEUTRAL = [183, 197, 221], PDF_INK = [20, 24, 40];
const _dash = (doc, on) => { try { doc.setLineDashPattern(on ? [2, 2] : [], 0); } catch (e) {} };
function pdfTornado(doc, X, Y, W, H, data, fmt, baseM) {
  if (!data || !data.length) return;
  const labelW = 104, plotX = X + labelW, plotW = W - labelW - 10;
  let lo = Infinity, hi = -Infinity;
  data.forEach((d) => { lo = Math.min(lo, d.low, d.high); hi = Math.max(hi, d.low, d.high); });
  if (isFinite(baseM)) { lo = Math.min(lo, baseM); hi = Math.max(hi, baseM); }
  if (!isFinite(lo) || !isFinite(hi)) { lo = 0; hi = 1; }
  if (lo === hi) { lo -= 1; hi += 1; }
  const padd = (hi - lo) * 0.1; lo -= padd; hi += padd;
  const sx = (v) => plotX + ((v - lo) / (hi - lo)) * plotW;
  const n = data.length, rowH = H / n, barH = Math.min(14, rowH * 0.5);
  doc.setFontSize(6.5);
  for (let k = 0; k <= 4; k++) {
    const v = lo + (hi - lo) * k / 4, xx = sx(v);
    doc.setDrawColor.apply(doc, PDF_GRID); doc.setLineWidth(0.4); doc.line(xx, Y, xx, Y + H);
    doc.setTextColor.apply(doc, PDF_AXIS); doc.text(fmt(v), xx, Y + H + 11, { align: k === 0 ? "left" : k === 4 ? "right" : "center" });
  }
  if (isFinite(baseM)) { doc.setDrawColor.apply(doc, PDF_BLUE); doc.setLineWidth(0.8); _dash(doc, 1); doc.line(sx(baseM), Y, sx(baseM), Y + H); _dash(doc, 0); }
  doc.setFontSize(8);
  data.forEach((d, i) => {
    const cy = Y + i * rowH + rowH / 2;
    const x1 = sx(Math.min(d.low, d.high)), x2 = sx(Math.max(d.low, d.high));
    doc.setFillColor.apply(doc, d.breach ? PDF_RED : PDF_NEUTRAL);
    doc.roundedRect(x1, cy - barH / 2, Math.max(2, x2 - x1), barH, 1.5, 1.5, "F");
    doc.setTextColor.apply(doc, PDF_INK); doc.setFont("helvetica", "normal"); doc.setFontSize(8);
    doc.text(String(d.label), X + labelW - 8, cy + 2.6, { align: "right", maxWidth: labelW - 10 });
  });
}
function pdfHist(doc, X, Y, W, H, hist, floor) {
  const GL = 26, px = X + GL, pw = W - GL;
  doc.setDrawColor.apply(doc, PDF_GRID); doc.setLineWidth(0.5); doc.line(px, Y + H, px + pw, Y + H);
  if (!hist || !hist.length) { doc.setTextColor.apply(doc, PDF_AXIS); doc.setFontSize(8); doc.text("Run the Monte Carlo in the tool to populate this chart.", px + pw / 2, Y + H / 2, { align: "center" }); return; }
  const maxC = Math.max.apply(null, hist.map((b) => b.count).concat(1));
  doc.setFontSize(6.5);
  [0, 0.5, 1].forEach((fr) => { const yy = Y + H - fr * H; doc.setDrawColor.apply(doc, PDF_GRID); doc.setLineWidth(0.4); doc.line(px, yy, px + pw, yy); doc.setTextColor.apply(doc, PDF_AXIS); doc.text(String(Math.round(maxC * fr)), px - 3, yy + 2, { align: "right" }); });
  const bw = pw / hist.length;
  hist.forEach((b, i) => { const bh = (b.count / maxC) * H; doc.setFillColor.apply(doc, b.lo < floor ? PDF_RED : PDF_GREEN); doc.rect(px + i * bw + bw * 0.12, Y + H - bh, bw * 0.76, bh, "F"); });
  const xs = hist.map((b) => b.x), lo = Math.min.apply(null, xs), hi = Math.max.apply(null, xs);
  if (hi > lo) { const fx = px + ((floor - lo) / (hi - lo)) * pw; if (fx >= px && fx <= px + pw) { doc.setDrawColor.apply(doc, PDF_RED); doc.setLineWidth(0.8); doc.line(fx, Y, fx, Y + H); doc.setTextColor.apply(doc, PDF_RED); doc.setFontSize(6.5); doc.text("floor " + floor.toFixed(2) + "×", fx + 2, Y + 8); } }
  doc.setTextColor.apply(doc, PDF_AXIS); doc.setFontSize(6.5);
  for (let k = 0; k <= 4; k++) { const v = lo + (hi - lo) * k / 4, xx = px + pw * k / 4; doc.text(v.toFixed(2) + "×", xx, Y + H + 11, { align: k === 0 ? "left" : k === 4 ? "right" : "center" }); }
}
function pdfLines(doc, X, Y, W, H, sched, floor) {
  if (!sched || !sched.length) return;
  const GL = 30, GR = 36, px = X + GL, pw = W - GL - GR;
  const years = sched.map((s) => s.year), dscrs = sched.map((s) => s.dscr).filter(isFinite), cfs = sched.map((s) => s.cashFlow);
  const xLo = Math.min.apply(null, years), xHi = Math.max.apply(null, years);
  const dHi = (Math.max.apply(null, dscrs.concat(floor)) * 1.12) || 1;
  const cLo = Math.min.apply(null, cfs.concat(0)), cHi = (Math.max.apply(null, cfs) * 1.12) || 1;
  const sx = (yv) => px + ((yv - xLo) / ((xHi - xLo) || 1)) * pw;
  const sdy = (v) => Y + H - (v / (dHi || 1)) * H;
  const scy = (v) => Y + H - ((v - cLo) / ((cHi - cLo) || 1)) * H;
  doc.setFontSize(6.5);
  for (let g = 0; g <= 3; g++) {
    const yy = Y + (H / 3) * g; doc.setDrawColor.apply(doc, PDF_GRID); doc.setLineWidth(0.4); doc.line(px, yy, px + pw, yy);
    doc.setTextColor.apply(doc, PDF_BLUE); doc.text((dHi * (1 - g / 3)).toFixed(1) + "×", px - 3, yy + 2, { align: "right" });
    doc.setTextColor.apply(doc, PDF_PINK); doc.text("$" + ((cLo + (cHi - cLo) * (1 - g / 3)) / 1e3).toFixed(0) + "k", px + pw + 3, yy + 2);
  }
  doc.setDrawColor.apply(doc, PDF_RED); doc.setLineWidth(0.7); _dash(doc, 1); doc.line(px, sdy(floor), px + pw, sdy(floor)); _dash(doc, 0);
  doc.setTextColor.apply(doc, PDF_RED); doc.setFontSize(6.5); doc.text("floor " + floor.toFixed(2) + "×", px + 3, sdy(floor) - 2.5);
  doc.setDrawColor.apply(doc, PDF_PINK); doc.setLineWidth(1.4);
  for (let i = 1; i < sched.length; i++) doc.line(sx(sched[i - 1].year), scy(sched[i - 1].cashFlow), sx(sched[i].year), scy(sched[i].cashFlow));
  doc.setDrawColor.apply(doc, PDF_BLUE); doc.setLineWidth(1.4);
  for (let i = 1; i < sched.length; i++) { if (isFinite(sched[i].dscr) && isFinite(sched[i - 1].dscr)) doc.line(sx(sched[i - 1].year), sdy(sched[i - 1].dscr), sx(sched[i].year), sdy(sched[i].dscr)); }
  doc.setTextColor.apply(doc, PDF_AXIS); doc.setFontSize(6.5);
  const ny = Math.min(5, sched.length);
  for (let k = 0; k < ny; k++) { const yr = Math.round(xLo + (xHi - xLo) * k / (ny - 1)); doc.text("Yr " + yr, sx(yr), Y + H + 11, { align: k === 0 ? "left" : k === ny - 1 ? "right" : "center" }); }
  doc.setFontSize(7); doc.setTextColor.apply(doc, PDF_BLUE); doc.text("— DSCR (left axis)", px, Y - 5);
  doc.setTextColor.apply(doc, PDF_PINK); doc.text("— Cash flow (right axis)", px + 96, Y - 5);
}

function pdfStack(doc, X, Y, W, segs, usesVal) {
  const total = segs.reduce((a, sg) => a + sg.value, 0) || 1;
  const scale = W / Math.max(total, usesVal);
  let cx = X; const Hh = 22;
  segs.forEach((sg) => { const sw = Math.max(0, sg.value) * scale; doc.setFillColor.apply(doc, sg.color); doc.rect(cx, Y, sw, Hh, "F"); if (sw > 26) { doc.setTextColor(255, 255, 255); doc.setFont("helvetica", "bold"); doc.setFontSize(7.5); doc.text(Math.round((sg.value / usesVal) * 100) + "%", cx + sw / 2, Y + 14, { align: "center" }); } cx += sw; });
  const ux = X + usesVal * scale; doc.setDrawColor(20, 24, 40); doc.setLineWidth(1.2); _dash(doc, 1); doc.line(ux, Y - 4, ux, Y + Hh + 4); _dash(doc, 0);
  doc.setTextColor(20, 24, 40); doc.setFont("helvetica", "bold"); doc.setFontSize(7); doc.text("Uses (TDC)", ux, Y - 6, { align: ux > X + W * 0.7 ? "right" : "center" });
}

function ChartTip({ active, payload, label, fmt }) {
  if (!active || !payload || !payload.length) return null;
  const f = (x) => (fmt ? fmt(x) : x);
  return (
    <div style={{ background: "#FFFFFF", boxShadow: "0 6px 20px rgba(11,27,74,.12)", border: `1px solid ${LINE}`, borderRadius: 8, padding: "8px 11px", fontSize: 12 }}>
      {label != null && typeof label !== "function" && <div style={{ color: MUTE, marginBottom: 4, fontFamily: "var(--mono)" }}>{label}</div>}
      {payload.map((p, i) => {
        const nm = typeof p.name === "string" ? p.name : null;
        const d = p.payload || {};
        const v = (d.low != null && d.high != null) ? `${f(d.low)} – ${f(d.high)}`
          : Array.isArray(p.value) ? `${f(p.value[0])} – ${f(p.value[1])}` : f(p.value);
        return (
          <div key={i} style={{ color: TEXT, fontFamily: "var(--mono)" }}>
            {nm ? `${nm}: ` : ""}{v}
          </div>
        );
      })}
    </div>
  );
}

const FIELD_GROUPS = [
  { title: "Sources & uses", fields: [
    ["totalDevelopmentCost", "Total development cost", "$M"],
    ["softSourcesAvailable", "Soft / gap sources", "$M"],
    ["annualCredits", "Annual tax credits", "$M"],
    ["taxCreditPrice", "Tax-credit price", "price"],
  ] },
  { title: "Units & income", fields: [
    ["unitCount", "Unit count", "int"],
    ["grossPotentialRent", "Gross potential rent", "$M"],
    ["otherIncome", "Other income", "$M"],
    ["operatingExpenses", "Operating expenses", "$M"],
    ["replacementReservePerUnit", "Replacement reserve / unit", "$"],
    ["vacancyRate", "Vacancy", "%"],
    ["badDebtRate", "Bad debt", "%"],
  ] },
  { title: "Financing", fields: [
    ["perpInterestRate", "Perm interest rate", "%"],
    ["amortYears", "Amortization", "yr"],
    ["loanTermYears", "Loan term", "yr"],
    ["sizingDscr", "Min / sizing DSCR", "x"],
  ] },
  { title: "Growth & exit", fields: [
    ["rentGrowth", "Rent growth", "%"],
    ["opexGrowth", "OpEx growth", "%"],
    ["exitCapRate", "Exit cap rate", "%"],
    ["leaseUpDelayMonths", "Lease-up delay", "mo"],
    ["horizonYears", "Analysis horizon", "yr"],
  ] },
];
const _toDisp = (v, t) => (t === "$M" ? v / 1e6 : t === "%" ? v * 100 : v);
const _fromDisp = (v, t) => (t === "$M" ? v * 1e6 : t === "%" ? v / 100 : v);
const _step = (t) => (t === "$M" ? 0.1 : t === "%" ? 0.05 : t === "price" ? 0.005 : t === "x" ? 0.01 : 1);
const _unit = (t) => ({ "$M": "$M", "%": "%", x: "×", price: "$/cr", $: "$", yr: "yr", mo: "mo", int: "" }[t] || "");

function NumberField({ k, label, type, value, onChange }) {
  const disp = _toDisp(value, type), u = _unit(type);
  return (
    <label style={{ display: "block", marginBottom: 10 }}>
      <span style={{ display: "flex", justifyContent: "space-between", fontSize: 11, letterSpacing: ".03em", color: MUTE, marginBottom: 4 }}>
        <span style={{ textTransform: "uppercase" }}>{label}</span>{u ? <span style={{ color: FAINT }}>{u}</span> : null}
      </span>
      <input type="number" className="ptd-num" step={_step(type)}
        value={Number.isFinite(disp) ? +(+disp).toFixed(4) : ""}
        onChange={(e) => { const dv = parseFloat(e.target.value); onChange(k, isNaN(dv) ? 0 : _fromDisp(dv, type)); }} />
    </label>
  );
}
function TextField({ label, value, onChange, placeholder }) {
  return (
    <label style={{ display: "block", marginBottom: 10 }}>
      <span style={{ display: "block", fontSize: 11, letterSpacing: ".03em", textTransform: "uppercase", color: MUTE, marginBottom: 4 }}>{label}</span>
      <input type="text" className="ptd-num" value={value} placeholder={placeholder || ""} onChange={onChange} />
    </label>
  );
}
function LogoUpload({ logo, onLogo }) {
  const ref = useRef(null);
  const pick = (e) => { const f = e.target.files && e.target.files[0]; if (!f) return; const r = new FileReader(); r.onload = () => onLogo(r.result); r.readAsDataURL(f); };
  return (
    <div style={{ marginBottom: 12 }}>
      <span style={{ display: "block", fontSize: 11, letterSpacing: ".03em", textTransform: "uppercase", color: MUTE, marginBottom: 6 }}>Logo (PNG / JPG)</span>
      {logo ? (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <img src={logo} alt="logo" style={{ height: 34, maxWidth: 120, objectFit: "contain", background: "#fff", border: `1px solid ${LINE}`, borderRadius: 6, padding: 3 }} />
          <button type="button" className="ptd-seg" onClick={() => onLogo(null)}>Remove</button>
        </div>
      ) : (
        <button type="button" className="ptd-seg" onClick={() => ref.current && ref.current.click()}>Upload logo</button>
      )}
      <input ref={ref} type="file" accept="image/png,image/jpeg" onChange={pick} style={{ display: "none" }} />
    </div>
  );
}

export default function PressureTestDashboard() {
  const [inp, setInp] = useState(BASE_INPUTS);
  const setNum = (k, v) => setInp((s) => ({ ...s, [k]: v }));
  const [branding, setBranding] = useState({ project: "", developer: "", analyst: "", date: "", location: "", logo: null });
  const setBr = (k) => (e) => setBranding((st) => ({ ...st, [k]: e && e.target ? e.target.value : e }));
  const [tab, setTab] = useState("inputs");
  const [exporting, setExporting] = useState(false);
  const tornadoRef = useRef(null), mcRef = useRef(null), opRef = useRef(null), scenRef = useRef(null);
  const cfg = useMemo(() => ({ minDscr: inp.sizingDscr, minIrr: 0.06 }), [inp.sizingDscr]);

  /* fonts: Futura PT + system mono inherited from the site */

  const reduce = typeof window !== "undefined" && window.matchMedia
    ? window.matchMedia("(prefers-reduced-motion: reduce)").matches : false;

  /* live computations (cheap) */
  const base = useMemo(() => computeProForma(inp), [inp]);
  const constraints = useMemo(() => evaluateConstraints(base, cfg), [base, cfg]);
  const dealPass = allPass(constraints);

  const [tornadoMetric, setTornadoMetric] = useState("minDscr");
  const tornado = useMemo(
    () => runTornado(inp, {
      totalDevelopmentCost: 0.08, perpInterestRate: 0.16, opexGrowth: 0.4,
      vacancyRate: 0.4, taxCreditPrice: 0.08, exitCapRate: 0.16, rentGrowth: 0.5,
      leaseUpDelayMonths: 1,
    }, tornadoMetric),
    [inp, tornadoMetric]
  );

  const scenarios = useMemo(() => runScenarios(inp, [
    { name: "Base", overrides: {} },
    { name: "Downside", overrides: { opexGrowth: inp.opexGrowth + 0.01, vacancyRate: inp.vacancyRate + 0.02, leaseUpDelayMonths: 6 } },
    { name: "Severe", overrides: { opexGrowth: inp.opexGrowth + 0.02, perpInterestRate: inp.perpInterestRate + 0.01, totalDevelopmentCost: inp.totalDevelopmentCost * 1.08, leaseUpDelayMonths: 9 } },
  ], cfg), [inp, cfg]);

  /* ---- Monte Carlo (chunked, on demand) ---- */
  const [mc, setMc] = useState(null);
  const [mcProg, setMcProg] = useState(0);
  const [mcRunning, setMcRunning] = useState(false);
  const [stale, setStale] = useState(false);
  const runId = useRef(0);

  const runMonteCarlo = useCallback((iterations = 4000) => {
    const id = ++runId.current;
    setMcRunning(true); setMcProg(0); setStale(false);
    const rng = makeRng(20260626);
    const tri = (v, dn, up) => [v * (1 - dn), v, v * (1 + up)];
    const dists = {
      totalDevelopmentCost: tri(inp.totalDevelopmentCost, 0.0, 0.12),
      perpInterestRate: tri(inp.perpInterestRate, 0.07, 0.15),
      opexGrowth: [Math.max(0, inp.opexGrowth - 0.01), inp.opexGrowth, inp.opexGrowth + 0.025],
      vacancyRate: [Math.max(0.02, inp.vacancyRate - 0.02), inp.vacancyRate, inp.vacancyRate + 0.04],
      exitCapRate: tri(inp.exitCapRate, 0.1, 0.18),
      leaseUpDelayMonths: [0, inp.leaseUpDelayMonths, inp.leaseUpDelayMonths + 6],
    };
    const keys = Object.keys(dists);
    const minDscrs = [], gaps = [], irrs = [];
    const breach = { min_dscr: 0, investor_irr: 0, funding_gap: 0 };
    let any = 0, i = 0;
    const CHUNK = 400;
    const step = () => {
      if (id !== runId.current) return; // superseded
      const end = Math.min(i + CHUNK, iterations);
      for (; i < end; i++) {
        const draw = { ...inp };
        for (const k of keys) { const d = dists[k]; draw[k] = sampleTri(rng, d[0], d[1], d[2]); }
        const out = computeProForma(draw);
        const cs = evaluateConstraints(out, cfg);
        minDscrs.push(out.minDscr); gaps.push(out.fundingGap);
        if (out.investorIrr != null) irrs.push(out.investorIrr);
        let b = false;
        for (const c of cs) if (!c.pass) { breach[c.id]++; b = true; }
        if (b) any++;
      }
      setMcProg(i / iterations);
      if (i < iterations) { requestAnimationFrame(step); return; }

      // histogram of min DSCR
      const loB = Math.min(...minDscrs), hiB = Math.max(...minDscrs);
      const NB = 22, w = (hiB - loB) / NB || 1;
      const hist = Array.from({ length: NB }, (_, b) => ({ x: loB + (b + 0.5) * w, lo: loB + b * w, count: 0 }));
      for (const v of minDscrs) { let b = Math.floor((v - loB) / w); if (b >= NB) b = NB - 1; if (b < 0) b = 0; hist[b].count++; }
      const pctile = (arr, p) => { const s = [...arr].sort((a, b) => a - b); return s[Math.min(s.length - 1, Math.floor(p * s.length))]; };
      setMc({
        iterations,
        probAnyBreach: any / iterations,
        breach: { min_dscr: breach.min_dscr / iterations, investor_irr: breach.investor_irr / iterations, funding_gap: breach.funding_gap / iterations },
        hist,
        dscrP: { p5: pctile(minDscrs, 0.05), p50: pctile(minDscrs, 0.5), p95: pctile(minDscrs, 0.95) },
        gapP: { p5: pctile(gaps, 0.05), p50: pctile(gaps, 0.5), p95: pctile(gaps, 0.95) },
      });
      setMcRunning(false);
    };
    requestAnimationFrame(step);
  }, [inp, cfg]);

  useEffect(() => {
    runMonteCarlo(4000);
    // run once on mount only — reruns are user-triggered via the Run/Re-run button,
    // with `stale` (below) flagging when inputs have drifted since the last run
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { setStale(true); }, [inp]);

  const floor = cfg.minDscr;
  const tornadoFmt = tornadoMetric === "fundingGap" ? fmtM : tornadoMetric === "investorIrr" ? ((v) => fmtPct(v)) : fmtX;
  const baseM = metricOf(base, tornadoMetric);

  const decision = useMemo(() => {
    const tdc = inp.totalDevelopmentCost, units = inp.unitCount || 1;
    const debt = base.loanProceeds, equity = base.equityRaised, soft = inp.softSourcesAvailable;
    const sources = debt + equity + soft, surplus = sources - tdc;
    let rateBreach = null;
    for (let rr = inp.perpInterestRate; rr <= inp.perpInterestRate + 0.06; rr += 0.00125) { if (computeProForma({ ...inp, perpInterestRate: rr }).minDscr < floor) { rateBreach = rr; break; } }
    const rateBps = rateBreach != null ? Math.round((rateBreach - inp.perpInterestRate) * 10000) : null;
    const rec = !dealPass ? "Restructure / pass" : (mc && mc.probAnyBreach > 0.35 ? "Proceed with conditions" : "Proceed");
    return { tdc, units, debt, equity, soft, sources, surplus, rateBps, rec };
  }, [base, inp, floor, dealPass, mc]);

  const exportPDF = useCallback(async () => {
    setExporting(true);
    try {
      const { default: JsPDF } = await import("jspdf");
      const doc = new JsPDF({ unit: "pt", format: "letter", compress: true });
      const _t = doc.text.bind(doc);
      doc.text = (str, ...r) => _t(typeof str === "string" ? str.replace(/[−–]/g, "-").replace(/…/g, "...").replace(/≥/g, ">=").replace(/≤/g, "<=").replace(/→/g, "to").replace(/[↑↓]/g, "") : str, ...r);
      const W = doc.internal.pageSize.getWidth(), H = doc.internal.pageSize.getHeight();
      const M = 48, CW = W - 2 * M;
      const NAVY = [10, 27, 74], SLATE = [90, 106, 140], LIGHT = [225, 231, 242], GREEN = [31, 157, 87], RED = [201, 42, 58], AMBER = [176, 120, 20], DARK = [20, 24, 40], TEAL = [13, 148, 136];
      const tc = (c) => doc.setTextColor(c[0], c[1], c[2]);
      const fK = (v) => (v == null ? "-" : (v < 0 ? "-" : "") + "$" + Math.round(Math.abs(v) / 1e3) + "k");
      const proj = branding.project || "Untitled Project";
      const dateStr = branding.date || new Date().toISOString().slice(0, 10);

      const tdc = inp.totalDevelopmentCost, units = inp.unitCount || 1;
      const debt = base.loanProceeds, equity = base.equityRaised, soft = inp.softSourcesAvailable;
      const sources = debt + equity + soft, surplus = sources - tdc;
      const SPEC = { totalDevelopmentCost: 0.08, perpInterestRate: 0.16, opexGrowth: 0.4, vacancyRate: 0.4, taxCreditPrice: 0.08, exitCapRate: 0.16, rentGrowth: 0.5, leaseUpDelayMonths: 1 };
      const dscrTor = runTornado(inp, SPEC, "minDscr").slice(0, 4).map((d) => ({ label: d.label, low: d.low, high: d.high, breach: d.low < floor }));
      let rateBreach = null;
      for (let rr = inp.perpInterestRate; rr <= inp.perpInterestRate + 0.06; rr += 0.00125) { if (computeProForma({ ...inp, perpInterestRate: rr }).minDscr < floor) { rateBreach = rr; break; } }
      const rateBps = rateBreach != null ? Math.round((rateBreach - inp.perpInterestRate) * 10000) : null;
      const pClear = mc ? 1 - mc.probAnyBreach : null;
      const rec = !dealPass ? "RESTRUCTURE / PASS" : (mc && mc.probAnyBreach > 0.35 ? "PROCEED WITH CONDITIONS" : "PROCEED");
      const RC = dealPass ? GREEN : RED;

      doc.setFillColor.apply(doc, NAVY); doc.rect(0, 0, W, 92, "F");
      if (branding.logo) { try { const pr = doc.getImageProperties(branding.logo); const lh = 44, lw = Math.min(150, pr.width * (lh / pr.height)); doc.addImage(branding.logo, branding.logo.indexOf("image/png") > -1 ? "PNG" : "JPEG", M, 24, lw, lh, undefined, "FAST"); } catch (e) { /* skip */ } }
      doc.setTextColor(255, 255, 255); doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.text("PRO FORMA PRESSURE TEST", W - M, 34, { align: "right" }); doc.setFontSize(15); doc.text(proj, W - M, 56, { align: "right" });
      doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(190, 202, 230); const meta = [branding.developer, branding.location, dateStr].filter(Boolean).join("   ·   "); if (meta) doc.text(meta, W - M, 74, { align: "right" });

      let y = 116;
      // ---- recommendation band ----
      const summ = "Base case " + (dealPass ? "clears all three covenants" : "breaches a covenant") + " (min DSCR " + fmtX(base.minDscr) + " vs " + fmtX(floor) + " floor; " + (surplus >= 0 ? "sources exceed uses by " + fmtM(surplus) : "funding gap of " + fmtM(-surplus)) + ")." + (mc ? " Under " + mc.iterations.toLocaleString() + "-run stress, " + fmtPct(pClear, 0) + " of scenarios hold." : "") + (surplus >= 0 ? " The stack absorbs a " + fmtPct(surplus / tdc, 1) + " cost overrun" + (rateBps ? " or a +" + rateBps + " bps rate move" : "") + " before a funding gap or DSCR breach." : "");
      const sumLines = doc.splitTextToSize(summ, CW - 28);
      const recBoxH = 50 + sumLines.length * 11;
      doc.setFillColor(dealPass ? 240 : 252, dealPass ? 249 : 240, dealPass ? 244 : 240); doc.setDrawColor.apply(doc, RC); doc.setLineWidth(1); doc.roundedRect(M, y, CW, recBoxH, 6, 6, "FD");
      tc(SLATE); doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.text("RECOMMENDATION", M + 14, y + 17);
      tc(RC); doc.setFontSize(17); doc.text(rec, M + 14, y + 38);
      tc(RC); doc.setFontSize(9); doc.text(dealPass ? "DEAL CLEARS — all covenants" : "COVENANT BREACH", W - M - 14, y + 17, { align: "right" });
      tc(DARK); doc.setFont("helvetica", "normal"); doc.setFontSize(8.5);
      sumLines.forEach((ln, i) => doc.text(ln, M + 14, y + 50 + i * 11));
      y += recBoxH + 16;

      // ---- card helper ----
      const card = (title, contentH, fn) => {
        const cardH = 40 + contentH + 16;
        if (y + cardH + 14 > H - 44) { doc.addPage(); y = 48; }
        doc.setFillColor(252, 253, 255); doc.setDrawColor.apply(doc, LIGHT); doc.setLineWidth(0.8); doc.roundedRect(M, y, CW, cardH, 8, 8, "FD");
        tc(DARK); doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.text(title, M + 16, y + 24);
        fn(M + 16, y + 42, CW - 32);
        y += cardH + 14;
      };

      // ---- covenant scorecard ----
      tc(DARK); doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.text("Covenant scorecard", M, y); y += 14;
      const irrOk = (base.investorIrr == null ? -1 : base.investorIrr) >= cfg.minIrr;
      const cov = [
        { k: "Min DSCR", act: fmtX(base.minDscr), req: "floor " + fmtX(floor), cush: (base.minDscr >= floor ? "+" : "") + (base.minDscr - floor).toFixed(2) + "× cushion", pass: base.minDscr >= floor },
        { k: "Investor IRR", act: fmtPct(base.investorIrr), req: "hurdle " + fmtPct(cfg.minIrr), cush: ((base.investorIrr || 0) - cfg.minIrr >= 0 ? "+" : "") + (((base.investorIrr || 0) - cfg.minIrr) * 100).toFixed(1) + " pp", pass: irrOk },
        { k: "Funding", act: surplus >= 0 ? fmtM(surplus) + " surplus" : fmtM(-surplus) + " gap", req: "min $0", cush: fmtPct(surplus / tdc, 1) + " of TDC", pass: surplus >= 0 },
      ];
      const cardW = (CW - 24) / 3;
      cov.forEach((c, i) => { const x = M + i * (cardW + 12); doc.setFillColor(248, 250, 252); doc.setDrawColor.apply(doc, c.pass ? GREEN : RED); doc.setLineWidth(0.8); doc.roundedRect(x, y, cardW, 60, 6, 6, "FD"); tc(SLATE); doc.setFont("helvetica", "normal"); doc.setFontSize(7.5); doc.text(c.k.toUpperCase(), x + 12, y + 16); tc(c.pass ? GREEN : RED); doc.setFont("helvetica", "bold"); doc.setFontSize(16); doc.text(String(c.act), x + 12, y + 36); tc(SLATE); doc.setFont("helvetica", "normal"); doc.setFontSize(7.5); doc.text(c.req + "  ·  " + c.cush, x + 12, y + 50); tc(c.pass ? GREEN : RED); doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.text(c.pass ? "PASS" : "FAIL", x + cardW - 12, y + 16, { align: "right" }); });
      y += 60 + 16;

      // ---- capital stack (framed) ----
      card("Capital stack — sources & uses", 70, (cx, cy, cw) => {
        pdfStack(doc, cx, cy + 14, cw, [{ value: debt, color: [0, 68, 235] }, { value: equity, color: TEAL }, { value: soft, color: AMBER }], tdc);
        let yy = cy + 50;
        doc.setFontSize(8); const legs = [["Senior debt", debt, [0, 68, 235]], ["LP / tax-credit equity", equity, TEAL], ["Soft / gap", soft, AMBER]];
        let lx = cx; legs.forEach((L) => { doc.setFillColor.apply(doc, L[2]); doc.rect(lx, yy - 6, 8, 8, "F"); tc(DARK); doc.setFont("helvetica", "normal"); doc.text(L[0] + "  " + fmtM(L[1]), lx + 11, yy); lx += doc.getTextWidth(L[0] + "  " + fmtM(L[1])) + 26; });
        yy += 17;
        tc(SLATE); doc.setFontSize(8.5); doc.text("Sources " + fmtM(sources) + "  ·  Uses " + fmtM(tdc) + "  ·  " + (surplus >= 0 ? "Surplus " : "GAP ") + fmtM(Math.abs(surplus)) + "    |    per unit:  TDC " + fK(tdc / units) + "  ·  Debt " + fK(debt / units) + "  ·  Equity " + fK(equity / units), cx, yy);
      });

      // ---- margin to breach ----
      tc(DARK); doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.text("Margin to breach — how much room before it breaks", M, y); y += 14;
      const margins = [
        ["Cost-overrun capacity", surplus >= 0 ? fmtM(surplus) + "  (" + fmtPct(surplus / tdc, 1) + ")" : "none — gap", surplus >= 0 ? "before a funding gap opens" : "deal is under-sourced"],
        ["Rate cushion", rateBps != null ? (rateBps <= 0 ? "0 bps" : "+" + rateBps + " bps") + " → " + fmtPct(rateBreach, 2) : ">600 bps", "min DSCR hits the " + fmtX(floor) + " floor"],
        ["Binding constraint", (base.minDscr - floor) <= ((base.investorIrr || 0) - cfg.minIrr) * 5 ? "Min DSCR" : "Investor IRR", "tightest covenant"],
      ];
      const mw = (CW - 24) / 3;
      margins.forEach((m, i) => { const x = M + i * (mw + 12); doc.setFillColor(252, 253, 255); doc.setDrawColor.apply(doc, LIGHT); doc.setLineWidth(0.8); doc.roundedRect(x, y, mw, 50, 6, 6, "FD"); tc(SLATE); doc.setFont("helvetica", "normal"); doc.setFontSize(7.5); doc.text(m[0].toUpperCase(), x + 12, y + 16); tc(DARK); doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.text(String(m[1]), x + 12, y + 32); tc(SLATE); doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.text(m[2], x + 12, y + 44); });
      y += 50 + 16;

      // ---- ranked drivers (framed) ----
      card("What breaks this deal — ranked drivers", 4 * 15, (cx, cy, cw) => {
        doc.setFontSize(8.5); let yy = cy;
        dscrTor.forEach((d, i) => { tc(d.breach ? RED : SLATE); doc.setFont("helvetica", "bold"); doc.text((i + 1) + ".", cx, yy); doc.text(String(d.label), cx + 14, yy); tc(DARK); doc.setFont("helvetica", "normal"); doc.text("min DSCR " + fmtX(d.low) + " - " + fmtX(d.high) + " across ±20%", cx + 152, yy); tc(d.breach ? RED : GREEN); doc.setFont("helvetica", "bold"); doc.text(d.breach ? "breaches floor on the downside" : "stays above floor", cx + cw, yy, { align: "right" }); yy += 15; });
      });

      // ---- charts (framed) ----
      const chartH = 150;
      const section = (title, subtitle, drawFn) => {
        const headH = subtitle ? 52 : 38;
        const cardH = headH + chartH + 42;
        if (y + cardH + 14 > H - 44) { doc.addPage(); y = 48; }
        doc.setFillColor(252, 253, 255); doc.setDrawColor.apply(doc, LIGHT); doc.setLineWidth(0.8); doc.roundedRect(M, y, CW, cardH, 8, 8, "FD");
        tc(DARK); doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.text(title, M + 16, y + 24);
        if (subtitle) { tc(SLATE); doc.setFont("helvetica", "normal"); doc.setFontSize(8.5); doc.text(subtitle, M + 16, y + 38); }
        drawFn(M + 16, y + headH + 14, CW - 32, chartH);
        y += cardH + 14;
      };
      const tFmt = tornadoMetric === "fundingGap" ? fmtM : tornadoMetric === "investorIrr" ? ((v) => fmtPct(v, 1)) : fmtX;
      const tMetricName = tornadoMetric === "fundingGap" ? "funding gap" : tornadoMetric === "investorIrr" ? "investor IRR" : "min DSCR";
      const tData = tornado.map((d) => ({ label: d.label, low: d.low, high: d.high, breach: tornadoMetric === "minDscr" ? d.low < floor : tornadoMetric === "fundingGap" ? d.high > 0 : d.low < cfg.minIrr }));
      section("Driver sensitivity — tornado (" + tMetricName + ")", "base " + tFmt(baseM) + "  ·  each bar spans -20% to +20% of the driver", (x, yy, w, h) => pdfTornado(doc, x, yy, w, h, tData, tFmt, baseM));
      section("Monte Carlo — min DSCR distribution", mc ? ("P5 " + fmtX(mc.dscrP.p5) + "   ·   median " + fmtX(mc.dscrP.p50) + "   ·   P95 " + fmtX(mc.dscrP.p95) + "   ·   P(breach) " + fmtPct(mc.probAnyBreach, 0)) : "Run the Monte Carlo in the tool for distribution stats", (x, yy, w, h) => pdfHist(doc, x, yy, w, h, mc ? mc.hist : null, floor));
      section("Operating waterfall — DSCR & cash flow", "min DSCR " + fmtX(base.minDscr) + "   ·   yr-" + base.schedule.length + " cash flow " + fmtM(base.schedule.length ? base.schedule[base.schedule.length - 1].cashFlow : null), (x, yy, w, h) => pdfLines(doc, x, yy, w, h, base.schedule, floor));

      // ---- MC decision summary (framed) ----
      if (mc) {
        card("Monte Carlo — decision summary", 4 * 15, (cx, cy, cw) => {
          doc.setFontSize(8.5);
          const mcrows = [["Probability all covenants clear", fmtPct(pClear, 0)], ["Breach mix", "Min DSCR " + fmtPct(mc.breach.min_dscr, 0) + "   ·   Funding gap " + fmtPct(mc.breach.funding_gap, 0) + "   ·   Investor IRR " + fmtPct(mc.breach.investor_irr, 0)], ["Min DSCR (P5 / median / P95)", fmtX(mc.dscrP.p5) + "  /  " + fmtX(mc.dscrP.p50) + "  /  " + fmtX(mc.dscrP.p95)], ["Funding gap (P5 / median / P95)", fmtM(-mc.gapP.p5) + "  /  " + fmtM(-mc.gapP.p50) + "  /  " + fmtM(-mc.gapP.p95) + "  (surplus +)"]];
          let yy = cy;
          mcrows.forEach((r) => { tc(SLATE); doc.setFont("helvetica", "normal"); doc.text(r[0], cx, yy); tc(DARK); doc.setFont("helvetica", "bold"); doc.text(r[1], cx + 200, yy); yy += 15; });
        });
      }

      if (y + 24 + scenarios.length * 18 > H - 44) { doc.addPage(); y = 48; }
      tc(DARK); doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.text("Bundled stress scenarios", M, y); y += 16;
      doc.setFontSize(8.5); tc(SLATE); doc.setFont("helvetica", "bold");
      const c2 = M + CW * 0.46, c3 = M + CW * 0.66, c4 = M + CW * 0.84;
      doc.text("SCENARIO", M, y); doc.text("MIN DSCR", c2, y, { align: "right" }); doc.text("GAP", c3, y, { align: "right" }); doc.text("IRR", c4, y, { align: "right" }); doc.text("VERDICT", W - M, y, { align: "right" });
      y += 5; doc.setDrawColor.apply(doc, LIGHT); doc.line(M, y, W - M, y); y += 13;
      scenarios.forEach((sc) => { doc.setFont("helvetica", "normal"); tc(DARK); doc.text(sc.name, M, y); tc(sc.outputs.minDscr >= floor ? GREEN : RED); doc.text(fmtX(sc.outputs.minDscr), c2, y, { align: "right" }); tc(sc.outputs.fundingGap <= 0 ? GREEN : RED); doc.text(fmtM(sc.outputs.fundingGap), c3, y, { align: "right" }); tc((sc.outputs.investorIrr == null ? -1 : sc.outputs.investorIrr) >= cfg.minIrr ? GREEN : RED); doc.text(fmtPct(sc.outputs.investorIrr), c4, y, { align: "right" }); tc(sc.pass ? GREEN : RED); doc.setFont("helvetica", "bold"); doc.text(sc.pass ? "CLEARS" : "BREACH", W - M, y, { align: "right" }); y += 17; });
      y += 16;

      if (y + 24 + 7 * 16 > H - 44) { doc.addPage(); y = 48; }
      tc(DARK); doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.text("Deal assumptions", M, y); y += 16;
      const rows = [["Units", String(inp.unitCount), "Total dev cost", fmtM(inp.totalDevelopmentCost)], ["Gross potential rent", fmtM(inp.grossPotentialRent), "Operating expenses", fmtM(inp.operatingExpenses)], ["Annual tax credits", fmtM(inp.annualCredits), "Tax-credit price", "$" + inp.taxCreditPrice.toFixed(3)], ["Soft / gap sources", fmtM(inp.softSourcesAvailable), "Perm interest rate", fmtPct(inp.perpInterestRate, 2)], ["Vacancy", fmtPct(inp.vacancyRate, 1), "Exit cap rate", fmtPct(inp.exitCapRate, 2)], ["Rent growth", fmtPct(inp.rentGrowth, 2), "OpEx growth", fmtPct(inp.opexGrowth, 2)], ["Lease-up delay", inp.leaseUpDelayMonths + " mo", "Min DSCR floor", fmtX(inp.sizingDscr)]];
      doc.setFontSize(9);
      rows.forEach((r) => { doc.setFont("helvetica", "normal"); tc(SLATE); doc.text(r[0], M, y); doc.text(r[2], M + CW / 2, y); doc.setFont("helvetica", "bold"); tc(DARK); doc.text(String(r[1]), M + CW / 2 - 14, y, { align: "right" }); doc.text(String(r[3]), W - M, y, { align: "right" }); y += 15.5; });
      y += 14;

      if (y + 110 > H - 44) { doc.addPage(); y = 48; }
      tc(DARK); doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.text("Methodology & disclaimer", M, y); y += 16;
      doc.setFont("helvetica", "normal"); doc.setFontSize(8.5); tc(SLATE);
      const disc = "Three stress lenses: single-driver sensitivity (tornado), bundled downside scenarios, and a " + (mc ? mc.iterations.toLocaleString() : "4,000") + "-iteration Monte Carlo — all evaluated against the lender DSCR floor, the investor IRR hurdle and the funding gap. ILLUSTRATIVE MODEL: loan sizing, equity timing and the IRR waterfall use simplified placeholder logic and are not funded terms. Validate every figure against a fully underwritten pro forma before relying on it for an investment, financing or entitlement decision.";
      doc.splitTextToSize(disc, CW).forEach((ln) => { doc.text(ln, M, y); y += 12; });

      // ===== GLOSSARY =====
      const glossary = [
        ["DSCR", "Debt Service Coverage Ratio — net operating income divided by annual debt service. Above 1.0x the income covers the loan; lenders require a cushion (the floor)."],
        ["Min DSCR", "The lowest DSCR across the hold period — usually year-1 during lease-up. This is the binding coverage test."],
        ["Investor IRR", "Internal Rate of Return to the LP (tax-credit) equity investor, weighting the timing of every cash flow."],
        ["Funding gap", "Total development cost minus all committed sources. Negative = surplus (over-sourced); positive = a shortfall to fill."],
        ["NOI", "Net Operating Income — effective gross income minus operating expenses, before debt service."],
        ["TDC", "Total Development Cost — the all-in cost to build the project."],
        ["Capital stack", "How the deal is financed: senior debt + LP equity + soft sources, set against the uses (TDC)."],
        ["LP / tax-credit equity", "Equity contributed by the investor that purchases the project's LIHTC tax credits."],
        ["Soft / gap sources", "Subordinate, low-cost financing — e.g. AHP, HOME funds, deferred developer fee, gap loans."],
        ["Exit cap rate", "The capitalization rate used to value the property at sale: NOI / cap rate = value."],
        ["Tornado", "Single-driver sensitivity — flex each input by +/-20% to rank which one moves the metric most."],
        ["Monte Carlo", "Thousands of randomized scenarios across the risky drivers, giving the odds the deal clears."],
        ["P(breach)", "The probability that at least one covenant fails across the simulated scenarios."],
        ["P5 / median / P95", "5th-percentile (downside) / median (typical) / 95th-percentile (upside) of the distribution."],
        ["Covenant", "A required threshold the deal must clear — the DSCR floor, the IRR hurdle, and no funding gap."],
        ["bps", "Basis points — 100 bps = 1.00%. Used for interest-rate moves."],
      ];
      const gColW = (CW - 32 - 26) / 2;
      doc.setFontSize(7.5);
      const gMeasure = (entries) => { let h = 0; entries.forEach(([t, d]) => { h += 9 + doc.splitTextToSize(d, gColW).length * 8.4 + 7; }); return h; };
      const gHalf = Math.ceil(glossary.length / 2);
      const gColA = glossary.slice(0, gHalf), gColB = glossary.slice(gHalf);
      const glossH = Math.max(gMeasure(gColA), gMeasure(gColB));
      const gCardH = 42 + glossH + 14;
      if (y + gCardH + 14 > H - 44) { doc.addPage(); y = 48; }
      doc.setFillColor(252, 253, 255); doc.setDrawColor.apply(doc, LIGHT); doc.setLineWidth(0.8); doc.roundedRect(M, y, CW, gCardH, 8, 8, "FD");
      tc(DARK); doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.text("Glossary — key terms", M + 16, y + 24);
      const gDrawCol = (entries, gx) => { let gy = y + 44; entries.forEach(([t, d]) => { tc(DARK); doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.text(t, gx, gy); tc(SLATE); doc.setFont("helvetica", "normal"); doc.setFontSize(7.5); const dl = doc.splitTextToSize(d, gColW); dl.forEach((ln, i) => doc.text(ln, gx, gy + 9 + i * 8.4)); gy += 9 + dl.length * 8.4 + 7; }); };
      gDrawCol(gColA, M + 16);
      gDrawCol(gColB, M + 16 + gColW + 26);
      y += gCardH + 14;

      const pages = doc.getNumberOfPages();
      for (let pg = 1; pg <= pages; pg++) {
        doc.setPage(pg);
        doc.setDrawColor.apply(doc, LIGHT); doc.setLineWidth(0.5); doc.line(M, H - 32, W - M, H - 32);
        doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(0, 68, 235); doc.text("BuildLab Consulting", M, H - 20);
        const bwBL = doc.getTextWidth("BuildLab Consulting");
        doc.setFont("helvetica", "normal"); doc.setFontSize(7.5); tc(SLATE); doc.text("  ·  buildlabconsulting.com", M + bwBL, H - 20);
        doc.text("Page " + pg + " of " + pages, W - M, H - 20, { align: "right" });
      }
      doc.save(proj.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase() + "-pressure-test.pdf");
    } catch (err) {
      console.error("PDF export failed", err);
      window.alert("Sorry — the PDF export ran into a problem. Please try again.");
    } finally {
      setExporting(false);
    }
  }, [branding, base, mc, inp, cfg, floor, dealPass, tornado, tornadoMetric, baseM, scenarios]);

  return (
    <div style={{ background: INK, color: TEXT, fontFamily: "var(--ui)", padding: "0 0 48px" }}>
      <style>{`
        :root{--ui:'Futura PT','Century Gothic',system-ui,sans-serif;--mono:ui-monospace,'SF Mono',Menlo,monospace;--px:26px;}
        .ptd-rail{width:290px;position:sticky;top:20px;align-self:flex-start;}
        @media (max-width:640px){:root{--px:14px;}.ptd-rail{width:100%;position:static;}}
        *{box-sizing:border-box;}
        .ptd-range{-webkit-appearance:none;appearance:none;width:100%;height:3px;border-radius:3px;background:${LINE};outline:none;}
        .ptd-range::-webkit-slider-thumb{-webkit-appearance:none;appearance:none;width:15px;height:15px;border-radius:50%;background:${ACCENT};cursor:pointer;border:2px solid ${INK};box-shadow:0 0 0 1px ${ACCENT};}
        .ptd-range::-moz-range-thumb{width:15px;height:15px;border-radius:50%;background:${ACCENT};cursor:pointer;border:2px solid ${INK};}
        .ptd-range:focus-visible{box-shadow:0 0 0 3px rgba(0,68,235,.28);}
        .ptd-seg{font-family:var(--mono);font-size:11px;padding:5px 9px;border-radius:6px;border:1px solid ${LINE};background:transparent;color:${MUTE};cursor:pointer;}
        .ptd-seg[data-on="1"]{background:${ACCENT};color:${INK};border-color:${ACCENT};font-weight:600;}
        .ptd-btn{font-family:var(--ui);font-weight:600;font-size:12.5px;padding:9px 16px;border-radius:8px;border:1px solid ${ACCENT};background:transparent;color:${ACCENT};cursor:pointer;letter-spacing:.02em;}
        .ptd-btn:hover{background:rgba(0,68,235,.08);}
        .ptd-btn:disabled{opacity:.45;cursor:default;}
        .ptd-num{width:100%;font-family:var(--mono);font-size:13px;color:${TEXT};background:#fff;border:1px solid ${LINE};border-radius:7px;padding:8px 10px;outline:none;}
        .ptd-num:focus{border-color:${ACCENT};box-shadow:0 0 0 3px rgba(0,68,235,.12);}
        .ptd-btn-primary{font-family:var(--ui);font-weight:700;font-size:12.5px;padding:9px 16px;border-radius:8px;border:1px solid ${ACCENT};background:${ACCENT};color:#fff;cursor:pointer;letter-spacing:.02em;}
        .ptd-btn-primary:hover{filter:brightness(1.06);}
        .ptd-btn-primary:disabled{opacity:.5;cursor:default;}
        table.ptd{width:100%;border-collapse:collapse;font-family:var(--mono);font-size:12.5px;}
        table.ptd th{text-align:right;color:${FAINT};font-weight:500;font-size:10.5px;letter-spacing:.06em;text-transform:uppercase;padding:0 0 10px;font-family:var(--ui);}
        table.ptd th:first-child,table.ptd td:first-child{text-align:left;}
        table.ptd td{padding:9px 0;border-top:1px solid ${LINE};color:${TEXT};}
        @media (prefers-reduced-motion: reduce){*{animation:none!important;transition:none!important;}}
      `}</style>

      {/* header (white-label) */}
      <div style={{ borderBottom: `1px solid ${LINE}`, padding: "18px var(--px)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0 }}>
          {branding.logo ? <img src={branding.logo} alt="" style={{ height: 38, maxWidth: 150, objectFit: "contain" }} /> : null}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 11, letterSpacing: ".18em", textTransform: "uppercase", color: ACCENT, fontWeight: 600 }}>Pro Forma Pressure Test</div>
            <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: "-.01em", marginTop: 2, color: TEXT }}>{branding.project || "Untitled Project"}</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, color: FAINT, fontFamily: "var(--mono)" }}>{inp.unitCount} units · {inp.horizonYears}-yr</span>
          <div style={{
            display: "flex", alignItems: "center", gap: 8, padding: "7px 14px", borderRadius: 20,
            background: dealPass ? "rgba(31,157,87,.12)" : "rgba(225,29,72,.10)",
            border: `1px solid ${dealPass ? PASS : BREACH}`,
          }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: dealPass ? PASS : BREACH }} />
            <span style={{ fontSize: 12.5, fontWeight: 600, color: dealPass ? PASS : BREACH, letterSpacing: ".03em" }}>
              {dealPass ? "DEAL CLEARS" : "COVENANT BREACH"}
            </span>
          </div>
          <button type="button" className="ptd-btn-primary" disabled={exporting} onClick={exportPDF}>{exporting ? "Generating…" : "Export PDF"}</button>
        </div>
      </div>

      <div style={{ display: "flex", gap: 20, padding: "20px var(--px)", alignItems: "flex-start", flexWrap: "wrap" }}>
        {/* ---- control rail ---- */}
        <div className="ptd-rail" style={{ flexShrink: 0, background: PANEL, border: `1px solid ${LINE}`, borderRadius: 12, padding: 18 }}>
          <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
            {[["inputs", "Deal inputs"], ["report", "Report"]].map(([k, l]) => (
              <button key={k} type="button" className="ptd-seg" data-on={tab === k ? 1 : 0} style={{ flex: 1, padding: "7px 0" }} onClick={() => setTab(k)}>{l}</button>
            ))}
          </div>

          {tab === "inputs" && (
            <>
              {FIELD_GROUPS.map((g) => (
                <div key={g.title} style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 10.5, letterSpacing: ".08em", textTransform: "uppercase", color: FAINT, fontWeight: 600, marginBottom: 8 }}>{g.title}</div>
                  {g.fields.map(([k, label, type]) => (
                    <NumberField key={k} k={k} label={label} type={type} value={inp[k]} onChange={setNum} />
                  ))}
                </div>
              ))}
              <button type="button" className="ptd-btn" style={{ width: "100%", marginTop: 4 }} onClick={() => setInp(BASE_INPUTS)}>Reset to sample deal</button>
            </>
          )}

          {tab === "report" && (
            <>
              <div style={{ fontSize: 10.5, letterSpacing: ".08em", textTransform: "uppercase", color: FAINT, fontWeight: 600, marginBottom: 10 }}>Report branding</div>
              <LogoUpload logo={branding.logo} onLogo={(d) => setBranding((st) => ({ ...st, logo: d }))} />
              <TextField label="Project name" value={branding.project} onChange={setBr("project")} placeholder="e.g. Maple Court Apartments" />
              <TextField label="Developer / firm" value={branding.developer} onChange={setBr("developer")} placeholder="Your company" />
              <TextField label="Prepared by" value={branding.analyst} onChange={setBr("analyst")} placeholder="Analyst name" />
              <TextField label="Date" value={branding.date} onChange={setBr("date")} placeholder="2026-06-26" />
              <TextField label="Location" value={branding.location} onChange={setBr("location")} placeholder="City, State" />
              <div style={{ fontSize: 11, color: FAINT, lineHeight: 1.5, marginTop: 8 }}>These appear on the PDF cover. Nothing is uploaded — it stays in your browser.</div>
            </>
          )}
        </div>

        {/* ---- analytics canvas ---- */}
        <div style={{ flex: 1, minWidth: 320, display: "flex", flexDirection: "column", gap: 20 }}>
          {/* decision summary */}
          <div style={{ background: dealPass ? "rgba(31,157,87,0.06)" : "rgba(225,29,72,0.05)", border: `1px solid ${dealPass ? PASS : BREACH}`, borderRadius: 12, padding: "16px 18px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8 }}>
              <div>
                <div style={{ fontSize: 10.5, letterSpacing: ".08em", textTransform: "uppercase", color: FAINT, fontWeight: 600 }}>Recommendation</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: dealPass ? PASS : BREACH, lineHeight: 1.1 }}>{decision.rec}</div>
              </div>
              <div style={{ fontSize: 12, color: dealPass ? PASS : BREACH, fontWeight: 600, letterSpacing: ".02em" }}>{dealPass ? "DEAL CLEARS — all covenants" : "COVENANT BREACH"}</div>
            </div>
            <div style={{ fontSize: 13, color: TEXT, marginTop: 8, lineHeight: 1.55 }}>
              Min DSCR {fmtX(base.minDscr)} vs {fmtX(floor)} floor · {decision.surplus >= 0 ? `${fmtM(decision.surplus)} surplus` : `${fmtM(-decision.surplus)} gap`}
              {mc ? ` · ${fmtPct(1 - mc.probAnyBreach, 0)} of stress scenarios clear` : " · running…"}.
              {decision.surplus >= 0 ? ` Absorbs a ${fmtPct(decision.surplus / decision.tdc, 1)} cost overrun${decision.rateBps ? ` or a +${decision.rateBps} bps rate move` : ""} before it breaks.` : ""}
            </div>
            <div style={{ display: "flex", height: 10, borderRadius: 5, overflow: "hidden", marginTop: 12, border: `1px solid ${LINE}` }}>
              <div style={{ width: `${Math.max(0, decision.debt / decision.sources * 100)}%`, background: "#0044EB" }} title="Senior debt" />
              <div style={{ width: `${Math.max(0, decision.equity / decision.sources * 100)}%`, background: "#0D9488" }} title="LP / tax-credit equity" />
              <div style={{ width: `${Math.max(0, decision.soft / decision.sources * 100)}%`, background: "#E08A2C" }} title="Soft / gap" />
            </div>
            <div style={{ fontSize: 11, color: MUTE, marginTop: 6, fontFamily: "var(--mono)" }}>
              Debt {fmtM(decision.debt)} · LP equity {fmtM(decision.equity)} · Soft {fmtM(decision.soft)} · per-unit TDC ${Math.round(decision.tdc / decision.units / 1e3)}k
            </div>
          </div>
          {/* verdict strip */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 14 }}>
            <Kpi label="Min DSCR" value={fmtX(base.minDscr)} color={dscrColor(base.minDscr, floor)}
              sub={`floor ${floor.toFixed(2)} · ${base.minDscr >= floor ? "+" : ""}${(base.minDscr - floor).toFixed(2)}`} />
            <Kpi label="Funding gap" value={fmtM(base.fundingGap)} color={gapColor(base.fundingGap)}
              sub={base.fundingGap <= 0 ? "fully sourced" : "shortfall"} />
            <Kpi label="Investor IRR" value={fmtPct(base.investorIrr)} color={irrColor(base.investorIrr, cfg.minIrr)}
              sub={`hurdle ${fmtPct(cfg.minIrr)}`} />
            <Kpi label="P(breach)" value={mc ? fmtPct(mc.probAnyBreach, 0) : "—"} color={mc ? (mc.probAnyBreach > 0.25 ? BREACH : mc.probAnyBreach > 0.1 ? MARGIN : PASS) : MUTE}
              sub={mc ? `${mc.iterations.toLocaleString()} sims` : "running…"} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }} className="ptd-grid">
            {/* tornado */}
            <div ref={tornadoRef}>
            <Panel title="Tornado — driver sensitivity"
              note="Each bar spans the metric from −20% to +20% of the driver. Longest bar = sharpest knife."
              right={
                <div style={{ display: "flex", gap: 6 }}>
                  {[["minDscr", "DSCR"], ["fundingGap", "Gap"], ["investorIrr", "IRR"]].map(([k, lbl]) => (
                    <button key={k} className="ptd-seg" data-on={tornadoMetric === k ? 1 : 0} onClick={() => setTornadoMetric(k)}>{lbl}</button>
                  ))}
                </div>
              }>
              <ResponsiveContainer width="100%" height={Math.max(220, tornado.length * 34)}>
                <BarChart layout="vertical" data={tornado} margin={{ left: 8, right: 16, top: 4, bottom: 4 }}>
                  <CartesianGrid horizontal={false} stroke={LINE} />
                  <XAxis type="number" stroke={FAINT} tick={{ fontSize: 10, fill: MUTE, fontFamily: "var(--mono)" }}
                    tickFormatter={(v) => tornadoMetric === "fundingGap" ? "$" + (v / 1e6).toFixed(0) + "M" : tornadoMetric === "investorIrr" ? (v * 100).toFixed(0) + "%" : v.toFixed(2)} />
                  <YAxis type="category" dataKey="label" width={120} stroke={FAINT}
                    tick={{ fontSize: 10.5, fill: TEXT, fontFamily: "var(--ui)" }} />
                  <Tooltip content={(p) => <ChartTip {...p} fmt={tornadoFmt} />} cursor={{ fill: "rgba(0,0,0,.04)" }} />
                  <ReferenceLine x={baseM} stroke={ACCENT} strokeDasharray="3 3" />
                  <Bar dataKey={(d) => [d.low, d.high]} isAnimationActive={!reduce} radius={3}>
                    {tornado.map((d, i) => {
                      const crosses = tornadoMetric === "minDscr" ? d.low < floor
                        : tornadoMetric === "fundingGap" ? d.high > 0
                        : d.low < cfg.minIrr;
                      return <Cell key={i} fill={crosses ? BREACH : "#B7C5DD"} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Panel>
            </div>

            {/* monte carlo histogram */}
            <div ref={mcRef}>
            <Panel title="Monte Carlo — min DSCR distribution"
              note={mc ? `P5 ${fmtX(mc.dscrP.p5)} · median ${fmtX(mc.dscrP.p50)} · P95 ${fmtX(mc.dscrP.p95)}` : "sampling triangular distributions on the risky drivers…"}
              right={<button className="ptd-btn" disabled={mcRunning} onClick={() => runMonteCarlo(4000)}>{mcRunning ? `${Math.round(mcProg * 100)}%` : stale ? "Re-run" : "Run"}</button>}>
              <ResponsiveContainer width="100%" height={Math.max(220, tornado.length * 34)}>
                <BarChart data={mc?.hist ?? []} margin={{ left: 0, right: 8, top: 4, bottom: 4 }}>
                  <CartesianGrid vertical={false} stroke={LINE} />
                  <XAxis dataKey="x" stroke={FAINT} tick={{ fontSize: 10, fill: MUTE, fontFamily: "var(--mono)" }}
                    tickFormatter={(v) => v.toFixed(2)} interval={3} />
                  <YAxis stroke={FAINT} tick={{ fontSize: 10, fill: MUTE, fontFamily: "var(--mono)" }} width={34} />
                  <Tooltip content={(p) => <ChartTip {...p} fmt={(v) => v + " runs"} />} cursor={{ fill: "rgba(0,0,0,.04)" }}
                    labelFormatter={(v) => "DSCR " + (+v).toFixed(2)} />
                  {mc && mc.hist.length > 0 && (
                    <ReferenceLine
                      x={mc.hist.reduce((best, d) => Math.abs(d.x - floor) < Math.abs(best - floor) ? d.x : best, mc.hist[0].x)}
                      stroke={BREACH} strokeWidth={1.5}
                      label={{ value: `floor ${floor.toFixed(2)}`, fill: BREACH, fontSize: 10, position: "top", fontFamily: "var(--mono)" }} />
                  )}
                  <Bar dataKey="count" isAnimationActive={!reduce}>
                    {(mc?.hist ?? []).map((d, i) => <Cell key={i} fill={d.lo < floor ? BREACH : PASS} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Panel>
            </div>
          </div>

          {/* 15-year operating line */}
          <div ref={opRef}>
          <Panel title="Operating waterfall — DSCR & cash flow"
            note="DSCR on the left, annual cash flow on the right. Year-1 reflects the lease-up haircut.">
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={base.schedule} margin={{ left: 4, right: 8, top: 8, bottom: 4 }}>
                <CartesianGrid stroke={LINE} vertical={false} />
                <XAxis dataKey="year" stroke={FAINT} tick={{ fontSize: 10, fill: MUTE, fontFamily: "var(--mono)" }} />
                <YAxis yAxisId="d" stroke={FAINT} tick={{ fontSize: 10, fill: MUTE, fontFamily: "var(--mono)" }} width={40} domain={[0, "auto"]} tickFormatter={(v) => v.toFixed(1)} />
                <YAxis yAxisId="c" orientation="right" stroke={FAINT} tick={{ fontSize: 10, fill: MUTE, fontFamily: "var(--mono)" }} width={48} tickFormatter={(v) => "$" + (v / 1e3).toFixed(0) + "k"} />
                <Tooltip content={(p) => <ChartTip {...p} fmt={(v) => typeof v === "number" && v > 100 ? fmtM(v) : fmtX(v)} />} labelFormatter={(v) => "Year " + v} cursor={{ stroke: LINE }} />
                <Legend wrapperStyle={{ fontSize: 11, fontFamily: "var(--mono)", color: MUTE }} />
                <ReferenceLine yAxisId="d" y={floor} stroke={BREACH} strokeDasharray="4 4" />
                <Line yAxisId="d" type="monotone" dataKey="dscr" name="DSCR" stroke={ACCENT} strokeWidth={2} dot={false} isAnimationActive={!reduce} />
                <Line yAxisId="c" type="monotone" dataKey="cashFlow" name="Cash flow" stroke={SECONDARY} strokeWidth={2} dot={false} isAnimationActive={!reduce} />
              </LineChart>
            </ResponsiveContainer>
          </Panel>
          </div>

          {/* scenarios */}
          <div ref={scenRef}>
          <Panel title="Scenarios — bundled stress" note="Several drivers move together. This is where compounding failures surface that a single-variable sweep misses.">
            <table className="ptd">
              <thead>
                <tr><th>Scenario</th><th>Min DSCR</th><th>Funding gap</th><th>Investor IRR</th><th>Verdict</th></tr>
              </thead>
              <tbody>
                {scenarios.map((s) => (
                  <tr key={s.name}>
                    <td style={{ fontFamily: "var(--ui)", fontWeight: 500 }}>{s.name}</td>
                    <td style={{ textAlign: "right", color: dscrColor(s.outputs.minDscr, floor) }}>{fmtX(s.outputs.minDscr)}</td>
                    <td style={{ textAlign: "right", color: gapColor(s.outputs.fundingGap) }}>{fmtM(s.outputs.fundingGap)}</td>
                    <td style={{ textAlign: "right", color: irrColor(s.outputs.investorIrr, cfg.minIrr) }}>{fmtPct(s.outputs.investorIrr)}</td>
                    <td style={{ textAlign: "right" }}>
                      <span style={{ fontFamily: "var(--ui)", fontSize: 11, fontWeight: 600, color: s.pass ? PASS : BREACH }}>
                        {s.pass ? "CLEARS" : "BREACH"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
          </div>

          <div style={{ fontSize: 11, color: FAINT, fontFamily: "var(--mono)", lineHeight: 1.6 }}>
            Loan sizing, equity timing and the IRR waterfall use placeholder logic — swap in your spec's math, then validate against a hand-underwritten deal before trusting any output.
          </div>
        </div>
      </div>

      <style>{`@media (max-width: 720px){.ptd-grid{grid-template-columns:1fr!important;}}`}</style>
    </div>
  );
}
